import {
  accountDeletionPreviewQuery,
  accountDeletionStatusQuery,
  cancelAccountDeletionMutation,
  removeAvatarMutation,
  scheduleAccountDeletionMutation,
  updateUserProfileMutation,
  uploadAvatarMutation,
} from '@affine/graphql';
import type {
  AuthenticationResponseJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import { Store } from '@toeverything/infra';

import type { GlobalState } from '../../storage';
import type { AuthSessionInfo } from '../entities/session';
import type { AuthProvider } from '../provider/auth';
import type { FetchService } from '../services/fetch';
import type { GraphQLService } from '../services/graphql';
import type { ServerService } from '../services/server';

export interface AccountProfile {
  id: string;
  email: string;
  name: string;
  hasPassword: boolean;
  avatarUrl: string | null;
  emailVerified: string | null;
}

export class AuthStore extends Store {
  constructor(
    private readonly fetchService: FetchService,
    private readonly gqlService: GraphQLService,
    private readonly globalState: GlobalState,
    private readonly serverService: ServerService,
    private readonly authProvider: AuthProvider
  ) {
    super();
  }

  watchCachedAuthSession() {
    return this.globalState.watch<AuthSessionInfo>(
      `${this.serverService.server.id}-auth`
    );
  }

  getCachedAuthSession() {
    return this.globalState.get<AuthSessionInfo>(
      `${this.serverService.server.id}-auth`
    );
  }

  setCachedAuthSession(session: AuthSessionInfo | null) {
    this.globalState.set(`${this.serverService.server.id}-auth`, session);
  }

  getClientNonce() {
    return this.globalState.get<string>('auth-client-nonce');
  }

  setClientNonce(nonce: string) {
    this.globalState.set('auth-client-nonce', nonce);
  }

  async fetchSession() {
    const url = `/api/auth/session`;
    const options: RequestInit = {
      credentials: 'include',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const res = await this.fetchService.fetch(url, options);
    const data = (await res.json()) as {
      user?: AccountProfile | null;
      needsTermsAcceptance?: boolean;
    };
    if (!res.ok)
      throw new Error('Get session fetch error: ' + JSON.stringify(data));
    return data; // Return null if data empty
  }

  async signInMagicLink(email: string, token: string) {
    return await this.authProvider.signInMagicLink(
      email,
      token,
      this.getClientNonce()
    );
  }

  async signInOauth(code: string, state: string, provider: string) {
    return await this.authProvider.signInOauth(
      code,
      state,
      provider,
      this.getClientNonce()
    );
  }

  async signInPassword(credential: {
    email: string;
    password: string;
    verifyToken?: string;
    challenge?: string;
  }) {
    return this.authProvider.signInPassword(credential);
  }

  async acceptTerms() {
    const res = await this.fetchService.fetch('/api/auth/accept-terms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error('Failed to accept terms');
    }
  }

  async signOut() {
    await this.authProvider.signOut();
  }

  // sl-wq59.6.4: WebAuthn passkey passthroughs (thin — orchestration lives
  // in AuthService, HTTP lives in AuthProvider impl).
  async webauthnRegisterOptions() {
    return this.authProvider.webauthnRegisterOptions();
  }

  async webauthnRegisterVerify(
    challengeRef: string,
    response: RegistrationResponseJSON,
    deviceName?: string
  ) {
    return this.authProvider.webauthnRegisterVerify(
      challengeRef,
      response,
      deviceName
    );
  }

  async webauthnLoginOptions() {
    return this.authProvider.webauthnLoginOptions();
  }

  async webauthnLoginVerify(
    challengeRef: string,
    response: AuthenticationResponseJSON
  ) {
    return this.authProvider.webauthnLoginVerify(challengeRef, response);
  }

  async webauthnListCredentials() {
    return this.authProvider.webauthnListCredentials();
  }

  async webauthnRemoveCredential(credentialId: string) {
    return this.authProvider.webauthnRemoveCredential(credentialId);
  }

  async uploadAvatar(file: File) {
    await this.gqlService.gql({
      query: uploadAvatarMutation,
      variables: {
        avatar: file,
      },
    });
  }

  async removeAvatar() {
    await this.gqlService.gql({
      query: removeAvatarMutation,
    });
  }

  async updateLabel(label: string) {
    await this.gqlService.gql({
      query: updateUserProfileMutation,
      variables: {
        input: {
          name: label,
        },
      },
    });
  }

  // sl-zvjz: Account-Deletion lifecycle (replaces hard-delete deleteAccount).

  async getAccountDeletionPreview() {
    const res = await this.gqlService.gql({
      query: accountDeletionPreviewQuery,
    });
    return res.accountDeletionPreview;
  }

  async getAccountDeletionStatus() {
    const res = await this.gqlService.gql({
      query: accountDeletionStatusQuery,
    });
    return res.accountDeletionStatus;
  }

  async scheduleAccountDeletion(reason?: string) {
    const res = await this.gqlService.gql({
      query: scheduleAccountDeletionMutation,
      variables: { reason },
    });
    return res.scheduleAccountDeletion;
  }

  async cancelAccountDeletion() {
    const res = await this.gqlService.gql({
      query: cancelAccountDeletionMutation,
    });
    return res.cancelAccountDeletion;
  }
}
